import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverKYC() {
  const { baseurl, token, AdminId, setIsUnauthorised } =
    useGlobalContext();

  const [kycDrivers, setKycDrivers] = useState([]);
  const [kycStatusLoading, setKycStatusLoading] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ================= FETCH ALL DRIVER KYC ================= */

  const fetchDriverKycDetails = useCallback(
    async (preserveLocalUpdates = true) => {
      setLoading(true);

      try {
        const res = await axios.post(
          `${baseurl}/api/Driver/v1.0/getAllDriverKycDetails`,
          { userid: "", adminId: AdminId },
          { headers: { Authorization: `Basic ${token}` } }
        );

        if (res.data.responseCode === "00") {
          const serverData = res.data.data || [];

          if (preserveLocalUpdates) {
            setKycDrivers((prev) =>
              serverData.map((serverDriver) => {
                const local = prev.find(
                  (d) =>
                    d.id === serverDriver.id ||
                    d.driver_code === serverDriver.driver_code
                );

                if (
                  local &&
                  ["Approved", "Rejected"].includes(local.status) &&
                  serverDriver.status === "Pending"
                ) {
                  return { ...serverDriver, status: local.status };
                }

                return serverDriver;
              })
            );
          } else {
            setKycDrivers(serverData);
          }
        } else if (res.data.responseCode === "401") {
          setIsUnauthorised(true);
        } else {
          toast.error(res.data.responseMessage);
        }
      } catch (err) {
        console.error(err);
        toast.error("Error fetching Driver KYC details");
      } finally {
        setLoading(false);
      }
    },
    [baseurl, token, AdminId, setIsUnauthorised]
  );

  /* ================= VIEW / UPDATE KYC ================= */

const handleKycAction = useCallback(
  async (payload) => {
    setKycStatusLoading(true);

    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/verifyDriverkycDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage);
        return res.data;
      }

      if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      }

      throw new Error(res.data.responseMessage);
    } catch (err) {
      toast.error(err.message || "KYC update failed");
      throw err;
    } finally {
      setKycStatusLoading(false);
    }
  },
  [baseurl, token, setIsUnauthorised]
);

  /* ================= SUBMIT KYC STEP ================= */

  const handleStepSubmit = async (stepNumber, stepData) => {
    try {
      setKycSubmitting(true);

      const payload = {
        userid: stepData.driver_code,
        adminId: AdminId,
        step: stepNumber.toString(),
        ...stepData,
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v2.0/addDriverKYCDetails`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.responseCode === "00") {
        toast.success(`Step ${stepNumber} saved successfully`);
        return true;
      }

      toast.error(res.data.responseMessage || "Failed to save step");
      return false;
    } catch (err) {
      console.error("KYC step submit error", err);
      toast.error("Error saving KYC data");
      return false;
    } finally {
      setKycSubmitting(false);
    }
  };

  return {
    kycDrivers,
    loading,
    fetchDriverKycDetails,

    kycStatusLoading,
    handleKycAction,

    kycSubmitting,
    handleStepSubmit,
  };
}
